import { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, getFirestore, limit, orderBy, query } from "firebase/firestore";
import { app, currentSession } from "../firebase";
import { Screen, ScreenHeader, Display, Card, Eyebrow, SectionLabel, btnIcon } from "../ui/components";
import { Ico } from "../ui/Ico";
import { C, mono } from "../ui/tokens";
import { useCountUp } from "../ui/useCountUp";
import { pickGreeting } from "../greetings";
import { getDevMode } from "../storage";

interface HistoryItem {
  receiptId: string;
  receiptDate: string;
  merchant: string;
  total: number;
  currency: string;
}

export function Home({
  employee, onStartScan, onOpenSettings,
}: {
  employee: string;
  onStartScan: () => void;
  onOpenSettings: () => void;
}) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [greeting] = useState(() => pickGreeting());
  const [devMode] = useState(() => getDevMode());
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function deleteEntry(receiptId: string) {
    setDeletingId(receiptId);
    try {
      const session = await currentSession();
      if (!session) return;
      const db = getFirestore(app);
      await deleteDoc(doc(db, "history", session.uid, "items", receiptId));
      setHistory((prev) => prev.filter((h) => h.receiptId !== receiptId));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
      setConfirmingId(null);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const session = await currentSession();
        if (!session) { setHistory([]); return; }
        const db = getFirestore(app);
        const ref = collection(doc(db, "history", session.uid), "items");
        const q = query(ref, orderBy("submittedAt", "desc"), limit(5));
        const snap = await getDocs(q);
        setHistory(snap.docs.map((d) => ({ receiptId: d.id, ...(d.data() as Omit<HistoryItem, "receiptId">) })));
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  const inCurrentQuarter = (d: string) => {
    const [y, m] = d.split("-").map(Number);
    return y === currentYear && Math.ceil(m / 3) === currentQuarter;
  };
  const quarterEntries = history.filter((h) => inCurrentQuarter(h.receiptDate));
  const total = quarterEntries.reduce((s, h) => s + h.total, 0);
  const animatedTotal = useCountUp(total);
  const periodLabel = `Q${currentQuarter} ${currentYear}`;
  const currency = quarterEntries[0]?.currency ?? history[0]?.currency ?? "CHF";

  return (
    <Screen>
      <ScreenHeader
        right={
          <button onClick={onOpenSettings} style={btnIcon} aria-label="Einstellungen">
            <Ico name="settings" size={18} />
          </button>
        }
      />

      <div>
        <div style={{ fontSize: 13, color: C.fg3, marginBottom: 6 }}>{greeting},</div>
        <Display>{employee}</Display>
      </div>

      {/* Monthly summary card */}
      <div style={{ marginTop: 24 }}>
        <Card style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
          padding: "20px 20px 22px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>Quartal · {periodLabel}</Eyebrow>
            <span style={{ fontSize: 12, color: C.fg3, fontFamily: mono }}>
              {quarterEntries.length.toString().padStart(2, "0")} Belege
            </span>
          </div>
          <div style={{
            marginTop: 14, display: "flex", alignItems: "baseline", gap: 8,
            fontFamily: mono, letterSpacing: "-0.02em",
          }}>
            <span style={{ fontSize: 13, color: C.fg3 }}>{currency}</span>
            <span style={{ fontSize: 36, fontWeight: 600, color: C.fg }}>{animatedTotal.toFixed(2)}</span>
          </div>
        </Card>
      </div>

      {/* Scan CTA */}
      <button onClick={onStartScan} style={{
        width: "100%", marginTop: 16, height: 84,
        borderRadius: 20, border: "none", cursor: "pointer",
        background: C.fg, color: "#000",
        padding: "0 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 32px rgba(0,0,0,0.5)",
      }}>
        <div style={{ textAlign: "left" }}>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: "0.18em",
            textTransform: "uppercase", color: "rgba(0,0,0,0.5)",
          }}>
            Neuer Beleg
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", marginTop: 4 }}>
            Quittung scannen
          </div>
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: 16,
          background: "#000", color: C.fg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Ico name="cam" size={22} color={C.fg} />
        </div>
      </button>

      {/* Recent */}
      <div style={{ marginTop: 32 }}>
        <SectionLabel right={history.length ? `${history.length} total` : undefined}>
          Letzte Einreichungen
        </SectionLabel>

        {loading ? (
          <div style={{ color: C.fg3, padding: "16px 4px", fontSize: 14 }}>…</div>
        ) : history.length === 0 ? (
          <div style={{
            padding: "28px 20px",
            border: `1px dashed ${C.border}`, borderRadius: 16,
            color: C.fg3, fontSize: 14,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: C.surface, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Ico name="receipt" size={20} color={C.fg3} />
            </div>
            <div style={{ marginTop: 4 }}>Noch keine Einreichungen.</div>
            <div style={{ fontSize: 12, color: C.fg4 }}>
              Tippe oben auf <em>Quittung scannen</em>.
            </div>
          </div>
        ) : (
          <div style={{
            display: "flex", flexDirection: "column", gap: 1,
            background: C.border, borderRadius: 16, overflow: "hidden",
          }}>
            {history.map((h) => {
              const confirming = confirmingId === h.receiptId;
              const deleting = deletingId === h.receiptId;
              return (
                <div key={h.receiptId} style={{
                  background: C.bg, padding: "16px 18px",
                  display: "flex", alignItems: "center", gap: 14,
                  color: C.fg,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: C.surface2, border: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Ico name="receipt" size={18} color={C.fg2} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 500,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {h.merchant || "Unbekannt"}
                    </div>
                    <div style={{ fontSize: 12, color: C.fg3, marginTop: 2 }}>
                      {h.receiptDate}
                    </div>
                  </div>
                  {confirming ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button
                        onClick={() => deleteEntry(h.receiptId)}
                        disabled={deleting}
                        style={{
                          fontFamily: mono, fontSize: 11, letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          padding: "8px 12px", borderRadius: 10,
                          background: C.dangerBg, color: C.danger,
                          border: "1px solid rgba(255,90,90,0.22)",
                          cursor: deleting ? "default" : "pointer",
                        }}
                      >
                        {deleting ? "…" : "Löschen"}
                      </button>
                      <button
                        onClick={() => setConfirmingId(null)}
                        disabled={deleting}
                        style={{
                          fontFamily: mono, fontSize: 11, letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          padding: "8px 12px", borderRadius: 10,
                          background: C.surface2, color: C.fg2,
                          border: `1px solid ${C.border}`,
                          cursor: deleting ? "default" : "pointer",
                        }}
                      >
                        Abbr.
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{
                        fontFamily: mono, fontSize: 15, fontWeight: 600,
                        color: C.fg, letterSpacing: "-0.01em",
                      }}>
                        {h.currency} {h.total.toFixed(2)}
                      </div>
                      {devMode && (
                        <button
                          onClick={() => setConfirmingId(h.receiptId)}
                          aria-label="Eintrag löschen"
                          style={{
                            width: 32, height: 32, borderRadius: 10,
                            background: "transparent", border: "none",
                            color: C.fg3, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            marginLeft: -4, padding: 0,
                          }}
                        >
                          <Ico name="trash" size={16} color={C.fg3} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Screen>
  );
}
